import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    RefreshCw,
    CheckCircle2,
    AlertTriangle,
    ShoppingBag,
    ArrowRight,
    QrCode,
    Settings,
} from 'lucide-react';
import {
    apiClient,
    API_ENDPOINTS,
    Button,
    Badge,
    Card,
    CardContent,
    Spinner,
    useAuthStore,
} from '@dukene/shared';
import { confirmAction } from '../components/modals/ConfirmModal';
import { WhatsAppLogo } from '../components/icons/BrandIcons';

interface MetaSyncStatus {
    lastSyncedAt: string | null;
    syncStatus: 'PENDING' | 'SYNCING' | 'SYNCED' | 'FAILED' | 'DELETED';
    lastError: string | null;
    metaCatalogId: string | null;
    metaBusinessId: string | null;
    stats: {
        total: number;
        synced: number;
        failed: number;
    };
    isConnected: boolean;
    whatsappLinked: boolean;
}

export default function WhatsAppCatalog() {
    const queryClient = useQueryClient();
    const activeStoreId = useAuthStore((state) => state.activeStoreId);

    // Fetch Meta Sync Status (which includes WhatsApp status)
    const { data: status, isLoading: isStatusLoading } = useQuery<MetaSyncStatus>({
        queryKey: ['meta-sync-status', activeStoreId],
        queryFn: () => apiClient.get<MetaSyncStatus>(API_ENDPOINTS.META.SYNC_STATUS),
        enabled: !!activeStoreId,
    });

    // Link WhatsApp Mutation
    const linkWhatsAppMutation = useMutation({
        mutationFn: () => apiClient.post(API_ENDPOINTS.META.LINK_WHATSAPP),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
        },
    });

    // Unlink WhatsApp Mutation
    const unlinkWhatsAppMutation = useMutation({
        mutationFn: () => apiClient.post(API_ENDPOINTS.META.UNLINK_WHATSAPP),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['meta-sync-status', activeStoreId] });
        },
    });

    if (isStatusLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Spinner className="w-8 h-8 text-emerald" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-gray-100">
                <div className="space-y-1">
                    <h1 className="text-3xl font-medium tracking-tight text-gray-900">WhatsApp Business Catalog</h1>
                    <p className="text-gray-500 text-base max-w-2xl">
                        Connect your products to WhatsApp. Allow customers to browse, cart and order directly within their chat.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-8 animate-in slide-in-from-bottom-4 duration-500">
                {/* WhatsApp Connection Card */}
                <Card className="overflow-hidden border-none shadow-[0_8px_30px_rgb(0,0,0,0.04)] bg-white transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                    <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] p-10 text-white relative overflow-hidden">
                        {/* Decorative Patterns */}
                        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-20 -mt-20 blur-2xl" />

                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 relative z-10">
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl">
                                        <WhatsAppLogo className="w-8 h-8" />
                                    </div>
                                    <h2 className="text-3xl font-bold">WhatsApp Channel</h2>
                                </div>
                                <p className="text-white/90 text-lg max-w-xl leading-relaxed">
                                    Turn your WhatsApp Business profile into a mobile store. Link your catalog and let customers shop 24/7 without leaving the conversation.
                                </p>
                            </div>

                            {!status?.isConnected ? (
                                <div className="bg-amber-400/20 text-amber-50 border border-amber-400/30 px-8 py-4 rounded-2xl backdrop-blur-md flex items-center gap-4">
                                    <AlertTriangle className="w-6 h-6" />
                                    <div className="space-y-1">
                                        <p className="font-bold text-lg leading-tight text-white">Meta Connection Required</p>
                                        <p className="text-sm opacity-80">You must connect Facebook/Instagram first.</p>
                                    </div>
                                </div>
                            ) : !status?.whatsappLinked ? (
                                <Button
                                    onClick={() => linkWhatsAppMutation.mutate()}
                                    disabled={linkWhatsAppMutation.isPending}
                                    className="bg-white text-[#25D366] hover:bg-white/90 font-bold h-14 px-10 rounded-2xl shadow-xl transition-all active:scale-[0.98] text-lg"
                                >
                                    {linkWhatsAppMutation.isPending ? 'Linking...' : 'Link WhatsApp Account'}
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            ) : (
                                <div className="flex flex-col items-end gap-3">
                                    <Badge className="bg-white/20 text-white border-white/30 px-6 py-3 text-base backdrop-blur-md rounded-xl">
                                        <CheckCircle2 className="w-5 h-5 mr-3" />
                                        Active on WhatsApp
                                    </Badge>
                                    <p className="text-white/60 text-sm">Synchronizing {status.stats.synced} products</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {status?.isConnected && status?.whatsappLinked && (
                        <CardContent className="p-10">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                                {/* Configuration */}
                                <div className="space-y-10">
                                    <div className="space-y-4">
                                        <h3 className="text-2xl font-bold text-gray-900">Visibility & Automation</h3>
                                        <p className="text-gray-500 text-lg leading-relaxed">
                                            Control how your products appear in the WhatsApp app.
                                        </p>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="flex items-center justify-between p-6 bg-gray-50 rounded-[24px] border border-gray-100 group transition-all hover:bg-white hover:shadow-lg hover:border-emerald/20">
                                            <div className="flex items-center gap-6">
                                                <div className="w-12 h-12 bg-emerald/10 rounded-2xl flex items-center justify-center text-emerald transition-transform group-hover:scale-110">
                                                    <ShoppingBag className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 text-lg">In-Chat Shopping Bag</p>
                                                    <p className="text-sm text-gray-500">Enable the shopping bag icon next to your business name</p>
                                                </div>
                                            </div>
                                            <div className="relative inline-flex h-7 w-12 items-center rounded-full bg-emerald">
                                                <span className="inline-block h-5 w-5 transform rounded-full bg-white transition translate-x-6 shadow-sm" />
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between p-6 bg-gray-50 rounded-[24px] border border-gray-100 group transition-all hover:bg-white hover:shadow-lg hover:border-blue-100">
                                            <div className="flex items-center gap-6">
                                                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 transition-transform group-hover:scale-110">
                                                    <RefreshCw className="w-6 h-6" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 text-lg">Instant Inventory Sync</p>
                                                    <p className="text-sm text-gray-500">Price and stock changes reflected immediately on WhatsApp</p>
                                                </div>
                                            </div>
                                            <div className="relative inline-flex h-7 w-12 items-center rounded-full bg-emerald">
                                                <span className="inline-block h-5 w-5 transform rounded-full bg-white transition translate-x-6 shadow-sm" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-6 space-y-4">
                                        <Button
                                            className="w-full h-14 rounded-2xl bg-gray-900 text-white hover:bg-black text-lg font-bold shadow-lg shadow-gray-200 transition-all hover:-translate-y-0.5"
                                            onClick={() => window.open(`https://wa.me/settings/catalog`, '_blank')}
                                        >
                                            <Settings className="w-5 h-5 mr-3" />
                                            Manage Catalog in Meta Business
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="w-full h-14 rounded-2xl border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-100 font-medium"
                                            onClick={async () => {
                                                const confirmed = await confirmAction({
                                                    title: 'Unlink Catalog',
                                                    message: 'Unlinking will remove the catalog from your WhatsApp profile. Keep catalog data?',
                                                    confirmText: 'Unlink',
                                                    cancelText: 'Cancel',
                                                });
                                                if (confirmed) {
                                                    unlinkWhatsAppMutation.mutate();
                                                }
                                            }}
                                        >
                                            Disconnect WhatsApp Catalog
                                        </Button>
                                    </div>
                                </div>

                                {/* Phone Preview */}
                                <div className="flex items-center justify-center">
                                    <div className="relative group">
                                        <div className="absolute inset-0 bg-emerald/20 blur-[80px] rounded-full scale-75 opacity-50 group-hover:opacity-100 transition-opacity" />

                                        {/* Mock Phone */}
                                        <div className="w-[300px] h-[610px] bg-[#111] rounded-[60px] border-[10px] border-[#222] shadow-2xl relative overflow-hidden flex flex-col z-10 transition-transform hover:scale-[1.02]">
                                            {/* Header */}
                                            <div className="h-20 bg-[#075E54] p-5 flex items-center gap-4">
                                                <div className="w-11 h-11 bg-gray-300 rounded-full" />
                                                <div className="space-y-1.5 flex-1">
                                                    <div className="w-28 h-2.5 bg-white/40 rounded-full" />
                                                    <div className="w-16 h-2 bg-white/20 rounded-full" />
                                                </div>
                                                <div className="w-5 h-5 bg-white/20 rounded-full" />
                                            </div>

                                            {/* Chat content */}
                                            <div className="flex-1 bg-[#ECE5DD] p-5 space-y-6">
                                                <div className="self-end bg-[#DCF8C6] p-4 rounded-2xl rounded-tr-none shadow-sm max-w-[85%] ml-auto">
                                                    <div className="w-40 h-2 bg-gray-900/10 rounded-full" />
                                                </div>

                                                {/* Catalog Item Preview */}
                                                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transform translate-y-4">
                                                    <div className="h-32 bg-gray-100 flex items-center justify-center">
                                                        <ShoppingBag className="w-10 h-10 text-gray-200" />
                                                    </div>
                                                    <div className="p-4 space-y-3">
                                                        <div className="w-32 h-3 bg-gray-900/80 rounded-full" />
                                                        <div className="flex justify-between items-center">
                                                            <div className="w-16 h-4 bg-emerald/10 font-bold rounded-full overflow-hidden relative">
                                                                <div className="absolute inset-y-0 left-2 right-2 bg-emerald/40 rounded-full my-auto h-1" />
                                                            </div>
                                                            <div className="h-8 px-4 bg-emerald text-white text-xs font-bold rounded-full flex items-center">
                                                                Add to Cart
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="self-start bg-white p-4 rounded-2xl rounded-tl-none shadow-sm max-w-[85%]">
                                                    <div className="w-24 h-2 bg-gray-900/10 rounded-full mb-2" />
                                                    <div className="w-48 h-2 bg-gray-900/10 rounded-full" />
                                                </div>
                                            </div>

                                            {/* Input area */}
                                            <div className="h-20 bg-[#F0F0F0] p-4 flex items-center gap-3">
                                                <div className="w-10 h-10 bg-white shadow-sm rounded-full flex items-center justify-center text-gray-400">
                                                    <ShoppingBag className="w-5 h-5 text-emerald" />
                                                </div>
                                                <div className="flex-1 h-11 bg-white rounded-full shadow-sm" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    )}
                </Card>

                {/* Growth Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Card className="p-10 border-none shadow-[0_4px_20px_rgb(0,0,0,0.02)] bg-white h-full relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
                            <QrCode className="w-32 h-32" />
                        </div>
                        <div className="space-y-6 relative z-10">
                            <div className="w-14 h-14 bg-emerald/10 rounded-2xl flex items-center justify-center text-emerald">
                                <QrCode className="w-7 h-7" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-2xl font-bold text-gray-900">Catalog Deep Links</h3>
                                <p className="text-gray-500 text-lg leading-relaxed">
                                    Direct customers straight to your WhatsApp catalog from Instagram ads, marketing emails, or offline QR codes.
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <div className="flex-1 h-12 bg-gray-50 rounded-xl px-5 flex items-center text-base font-mono text-gray-500 border border-gray-100">
                                    wa.me/c/{status?.metaBusinessId || '123456789'}
                                </div>
                                <Button className="h-12 px-6 bg-emerald hover:bg-emerald-600 text-white rounded-xl font-bold">Copy</Button>
                            </div>
                        </div>
                    </Card>

                    <Card className="p-10 border-none shadow-[0_4px_20px_rgb(0,0,0,0.02)] bg-white h-full group">
                        <div className="space-y-6">
                            <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                                <ShoppingBag className="w-7 h-7" />
                            </div>
                            <div className="space-y-3">
                                <h3 className="text-2xl font-bold text-gray-900">Conversational Commerce</h3>
                                <p className="text-gray-500 text-lg leading-relaxed">
                                    When customers message you with items in their cart, Dukene automatically parses the order and notifies you in real-time.
                                </p>
                            </div>
                            <Button variant="outline" className="w-full h-12 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50 font-medium">Learn how it works</Button>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
